import { resolveDeclineCopy } from 'src/utils/resolveBillingError';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { LEAD_AGENT_PAYWALL_EVENTS } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import CheckoutCopyGate from '../CheckoutCopyGate.component';
import { buildUpgradeRenewalBreakdown } from '../acceptanceModals.functions';
import type { tLeadAgentSelectableTier } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';
import { useUnlockTrialCapModal } from './UnlockTrialCapModal.hook';
import type { tUnlockTrialCapModalProps } from './UnlockTrialCapModal.types';

const NS = 'common/leadAgentUnlockModal';
const ENTRY_POINT = 'trial_cap_offer';

/**
 * Lead Agent trial-cap UNLOCK modal (RET-1633 / U10). OWNER-only — the trial-cap
 * banner only wires its `unlock_now` CTA to open this for owners. Option 1 of the
 * offer: an IMMEDIATE off-session charge that both accepts the tier and raises
 * the trial cap so the bot resumes. Separate component from the deferred
 * ConfirmTierModal (option 2) and the add-on DeclineModal (never option 3, R12).
 *
 * View machine (in the hook): confirm → processing → success | error | blocked.
 * The success copy says the charge is PROCESSING when provisioning is still
 * finishing (and always after a 3DS confirm) so it never claims the cap is live
 * before the webhook lands. Failure has NO grace: the Owner stays paused and can
 * retry (re-POSTs the same params) or update the card in Configuración >
 * Suscripción. `<Elements>` wraps the app tree, so the 3DS `useStripe` resolves
 * wherever the trial-cap banner container mounts.
 */
export default function UnlockTrialCapModal(props: tUnlockTrialCapModalProps) {
  const {
    workspaceId,
    offer,
    isNonMonthly,
    onClose,
    onUnlocked,
    onProcessingChange,
  } = props;
  const { t, ready: copyReady } = useTranslation(NS);
  // The billing-error copy (decline map) lives in its own namespace (RET-1372);
  // resolveDeclineCopy requires a `t` bound to it (RET-1972).
  const { t: tBilling } = useTranslation('settings/SubscriptionManager');
  const { addAlert } = useToastContext();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  // Verb by phase (R3): "extender" while the bot still runs (sighting), then
  // "desbloquear" once paused (capped). Drives the headline + submit copy.
  const phaseKey = offer.phase === 'capped' ? 'capped' : 'sighting';

  /*
   * MLA-74 — "se muestra solo el recomendado; los demás bajo 'Ver otros
   * planes'. Lidera con el recomendado pero muestra ambos precios y deja
   * cambiar" (explicitly, so Scale is not oversold). The modal used to render
   * every tier expanded, so the recommendation competed with itself.
   */
  const [showAllTiers, setShowAllTiers] = useState(false);

  /*
   * The charge is immediate and off-session, so it is consented to explicitly —
   * as the three sibling checkouts already do. Unchecked does NOT disable the
   * button: pressing it surfaces the error, which is the prototype's behaviour
   * (`#terms-error`, shown on submit).
   */
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  // Reuse the D8 `la_paywall_modal_opened` event with the `trial_cap_offer`
  // entry_point discriminator (the "reuse existing events with a discriminator"
  // convention — no new event name). Fired once per open.
  useEffect(() => {
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.MODAL_OPENED, {
      entry_point: ENTRY_POINT,
    });
    // Mount-only: one emit per open. Deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    view,
    tiers,
    selectedTier,
    selectedTierOffer,
    confirmDisabled,
    selectTier,
    confirm,
    projection,
    retryProjection,
  } = useUnlockTrialCapModal({ workspaceId, offer });

  // Funnel outcome events (RET-1614 pattern): terminal charge state fires once.
  // 'error' (retryable) and 'blocked' (4xx) both count as a failed attempt;
  // success carries the tier charged.
  useEffect(() => {
    if (view.status === 'success') {
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_SUCCEEDED, {
        entry_point: ENTRY_POINT,
        target_tier: view.tier.toLowerCase(),
      });
    } else if (view.status === 'error' || view.status === 'blocked') {
      trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_FAILED, {
        entry_point: ENTRY_POINT,
        target_tier: selectedTier.toLowerCase(),
        outcome: view.status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.status]);

  const isProcessing = view.status === 'processing';
  /*
   * The DEDUPING builder, not `buildRenewalBreakdown`. The projection endpoint
   * composes the recurring lines and then ADDS the requested tier without
   * deduping, so any payload that already lists the add-on counts it twice.
   *
   * For THIS modal the dedupe is defense, not a correction: a workspace still
   * in its trial has no Lead Agent row in purchased_products, so its payload
   * carries no Lead Agent line and the builder degrades to a no-op (verified
   * against production 2026-09-01; the tests pin the no-op as the live case).
   * It is still the right choice — the same endpoint double-counts for the
   * workspaces that HAVE bought the add-on, and this modal is one deferred
   * charge away from being read by one of them.
   */
  const breakdown = useMemo(
    () =>
      projection.status === 'ready'
        ? buildUpgradeRenewalBreakdown(projection.data)
        : null,
    [projection],
  );

  const hasTiers = tiers.length > 0;
  /*
   * Collapsed, only the recommended tier shows — but the SELECTED one always
   * does, or choosing "Scale" and collapsing would hide what you picked.
   */
  const visibleTiers = showAllTiers
    ? tiers
    : tiers.filter(
        (tierOffer) =>
          tierOffer.tier === offer.suggestedTier ||
          tierOffer.tier === selectedTier,
      );

  // Surface the in-flight state so the container can defer a workspace-switch
  // close until a charge finishes (REL-2).
  useEffect(() => {
    onProcessingChange?.(isProcessing);
  }, [isProcessing, onProcessingChange]);

  const amountLabel = useMemo(() => {
    if (!selectedTierOffer) return null;
    return formatPrice({
      price: selectedTierOffer.unlockAmountCents,
      currency: selectedTierOffer.currency,
    });
  }, [selectedTierOffer]);

  // Every guard that blocks a user action must surface feedback — a bare return
  // is an invisible dead button
  // (docs/solutions/patterns/reactivation-is-not-change-plan-per-intent-semantics.md).
  function handleClose() {
    if (isProcessing) {
      addAlert({ type: eAlertType.INFO, message: t('processing.wait') });
      return;
    }
    onClose();
  }

  function handleSelectTier(tier: tLeadAgentSelectableTier) {
    if (tier === selectedTier || isProcessing) return;
    selectTier(tier);
  }

  function handleConfirm() {
    if (confirmDisabled) return;
    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }
    // Gate the funnel event on the hook's synchronous in-flight latch: `confirm`
    // returns false when a same-tick double click already started the charge, so
    // UPGRADE_CONFIRMED fires exactly once per attempt (matching the single POST).
    if (!confirm()) return;
    trackEvent(LEAD_AGENT_PAYWALL_EVENTS.UPGRADE_CONFIRMED, {
      entry_point: ENTRY_POINT,
      target_tier: selectedTier.toLowerCase(),
    });
  }

  function handleSuccessDismiss() {
    if (view.status === 'success') {
      onUnlocked?.({ tier: view.tier, pending: view.pending });
    }
    onClose();
  }

  function handleUpdateCard() {
    navigate(
      `${RouterPaths.settings}?section=${SettingsTabs.SUBSCRIPTION}&cta_type=lead_agent_unlock`,
    );
  }

  // Sin la copy de este modal no hay nada que leer: `t()` devolvería
  // llaves crudas junto a montos correctos. Ver CheckoutCopyGate.
  if (!copyReady) {
    return (
      <CheckoutCopyGate
        className="LeadAgentUnlockModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
        onClose={handleClose}
      />
    );
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={handleClose}
      className="LeadAgentUnlockModal rounded-lg p-0 min-w-[34rem] max-w-[34rem]"
    >
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="text-lg font-semibold">{t(`title.${phaseKey}`)}</h3>
        <XMarkIcon
          onClick={handleClose}
          className="size-6 cursor-pointer text-neutral-500"
          data-testid="unlock-modal-close"
        />
      </div>

      {/* ---- SUCCESS ---- */}
      {view.status === 'success' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="unlock-modal-success"
        >
          <p className="text-base font-medium">{t('success.title')}</p>
          <p className="text-sm text-neutral-600">
            {view.pending ? t('success.processing') : t('success.resumed')}
          </p>
          <Button
            color="primary"
            size="sm"
            className="rounded self-end"
            onClick={handleSuccessDismiss}
            data-testid="unlock-modal-success-dismiss"
          >
            {t('success.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- BLOCKED (4xx business rule, NOT retryable) ---- */}
      {view.status === 'blocked' ? (
        <div
          className="p-6 flex flex-col gap-4"
          data-testid="unlock-modal-blocked"
        >
          <p className="text-base font-medium text-error">
            {t('blocked.title')}
          </p>
          <p className="text-sm text-neutral-600">
            {view.message ?? t('blocked.body')}
          </p>
          {view.code ? (
            <p
              className="text-xs text-neutral-400"
              data-testid="unlock-modal-blocked-code"
            >
              {view.code}
            </p>
          ) : null}
          <Button
            color="ghost"
            size="sm"
            className="rounded self-end"
            onClick={onClose}
            data-testid="unlock-modal-blocked-dismiss"
          >
            {t('blocked.dismiss')}
          </Button>
        </div>
      ) : null}

      {/* ---- CONFIRM / PROCESSING / ERROR share the picker + summary ---- */}
      {view.status === 'confirm' ||
      view.status === 'processing' ||
      view.status === 'error' ? (
        <>
          <div className="p-4 flex flex-col gap-4">
            {/*
              The headline sentence is gone (2026-08-31). It repeated the "Hoy ·
              extiende tu prueba" row word for word once that row existed, and
              the prototype — the visual reference §18 points at — has no such
              sentence.

              MLA-82 ("el subtítulo lidera con lo de HOY, no con el precio al
              año") still holds: its hazard is an ANNUAL figure scaring the
              customer, and no annual figure is ever shown here. Per MLA-77 the
              unlock is one month at the tier's standard MONTHLY price minus the
              coupon, the same amount at any periodicity, so the card's price is
              monthly too. Today's amount stays on its own bolded row.
            */}
            {!hasTiers ? (
              <p
                className="text-sm text-neutral-600"
                data-testid="unlock-modal-empty"
              >
                {t('empty.body')}
              </p>
            ) : null}

            {hasTiers ? (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t('yourPlan')}</span>
                {tiers.length > 1 ? (
                  <button
                    type="button"
                    className="text-sm text-primary underline underline-offset-2"
                    onClick={() => setShowAllTiers((shown) => !shown)}
                    data-testid="unlock-modal-toggle-tiers"
                  >
                    {showAllTiers ? t('hideOtherPlans') : t('showOtherPlans')}
                  </button>
                ) : null}
              </div>
            ) : null}

            {hasTiers ? (
              <div
                className="flex flex-col gap-2"
                data-testid="unlock-modal-plan-list"
              >
                {visibleTiers.map((tierOffer) => {
                  const { tier } = tierOffer;
                  const isSelected = tier === selectedTier;
                  const isRecommended = tier === offer.suggestedTier;
                  return (
                    <label
                      key={tier}
                      className={classnames(
                        'flex items-center gap-3 rounded border p-3 cursor-pointer',
                        {
                          'border-primary bg-primary/5': isSelected,
                          'border-base-300': !isSelected,
                        },
                      )}
                      data-testid={`unlock-tier-option-${tier}`}
                    >
                      <input
                        type="radio"
                        name="lead-agent-unlock-tier"
                        className="radio radio-primary radio-sm"
                        checked={isSelected}
                        onChange={() => handleSelectTier(tier)}
                        disabled={isProcessing}
                        data-testid={`unlock-tier-radio-${tier}`}
                      />
                      <span className="flex-1 flex flex-col">
                        <span className="flex items-center gap-2">
                          <span className="text-base font-semibold">
                            {t(`tier.${tier}`)}
                          </span>
                          {isRecommended ? (
                            <span
                              className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5"
                              data-testid={`unlock-tier-recommended-${tier}`}
                            >
                              {t('recommended')}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className="text-xs text-neutral-500"
                          data-testid={`unlock-tier-benefit-${tier}`}
                        >
                          {t(`tierBenefit.${tier}`)}
                        </span>
                      </span>
                      {/* The RECURRING price — what this tier costs from the
                          renewal on. What you pay today is its own line below;
                          MLA-82 keeps today leading and never the recurring
                          figure. */}
                      <span
                        className="text-base font-semibold whitespace-nowrap"
                        data-testid={`unlock-tier-unlock-${tier}`}
                      >
                        {t('perMonth', {
                          amount: formatPrice({
                            price: tierOffer.listAmountCents,
                            currency: tierOffer.currency,
                          }),
                        })}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : null}

            {/* Today's charge on its own line. MLA-77: one month at the
                tier's standard monthly price minus the coupon, a separate
                invoice charged immediately, the SAME amount at any
                periodicity. */}
            {amountLabel ? (
              <div
                className="flex items-center justify-between rounded border border-base-300 p-3"
                data-testid="unlock-modal-today"
              >
                <span className="text-sm">{t(`today.${phaseKey}`)}</span>
                <span className="text-sm font-semibold">{amountLabel}</span>
              </div>
            ) : null}

            {/* What the subscription BECOMES. The modal charged today and said
                nothing about the renewal; its three sibling checkouts have
                shown this breakdown all along. */}
            {breakdown ? (
              <div
                className="flex flex-col gap-2 rounded bg-base-200 p-3"
                data-testid="unlock-modal-renewal-breakdown"
              >
                <div>
                  <p className="text-sm font-semibold">{t('renewal.title')}</p>
                  {breakdown.renewalDate ? (
                    <p className="text-xs text-neutral-500">
                      {breakdown.renewalDate}
                    </p>
                  ) : null}
                </div>
                {breakdown.renewalLines.map((line) => (
                  <div
                    key={line.description}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      {line.description}
                      {line.isNew ? (
                        <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary-content">
                          {t('renewal.new')}
                        </span>
                      ) : null}
                    </span>
                    <span>{line.amount}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 border-t border-base-300 pt-2 text-sm font-bold">
                  <span>
                    {breakdown.renewalDate
                      ? t('renewal.totalOn', { date: breakdown.renewalDate })
                      : t('renewal.total')}
                  </span>
                  <span>{breakdown.renewalTotal}</span>
                </div>
              </div>
            ) : null}

            {projection.status === 'loading' ? (
              <p
                className="text-xs text-neutral-500"
                data-testid="unlock-modal-renewal-loading"
              >
                {t('renewal.loading')}
              </p>
            ) : null}

            {projection.status === 'error' ? (
              <button
                type="button"
                className="self-start text-xs text-primary underline"
                onClick={retryProjection}
                data-testid="unlock-modal-renewal-retry"
              >
                {t('renewal.retry')}
              </button>
            ) : null}

            <p
              className="text-xs text-neutral-500"
              data-testid="unlock-modal-discount-note"
            >
              {t(`discountNote.${phaseKey}`)}
            </p>

            {/* Non-monthly renewal note: secondary, never leads (R16). */}
            {isNonMonthly ? (
              <p
                className="text-xs text-neutral-500"
                data-testid="unlock-modal-renewal-note"
              >
                {t('renewalNote')}
              </p>
            ) : null}

            {/* Explicit consent to an immediate off-session charge. Unchecked
                does not disable the button — pressing it shows the error, as
                the prototype does. */}
            <label
              className={classnames('flex items-center gap-2 cursor-pointer', {
                'text-error': showTermsError && !termsAccepted,
              })}
              data-testid="unlock-modal-terms"
            >
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary shrink-0"
                checked={termsAccepted}
                disabled={isProcessing}
                onChange={(event) => {
                  setTermsAccepted(event.target.checked);
                  if (event.target.checked) setShowTermsError(false);
                }}
                data-testid="unlock-modal-terms-checkbox"
              />
              <span className="text-xs">{t('terms.label')}</span>
            </label>

            {showTermsError && !termsAccepted ? (
              <p
                className="text-xs text-error"
                role="alert"
                data-testid="unlock-modal-terms-error"
              >
                {t('terms.error')}
              </p>
            ) : null}

            {/* ---- Retryable error (decline / in-progress / transient) ---- */}
            {view.status === 'error' ? (
              <div
                className="flex flex-col gap-1 rounded border border-error/30 bg-error/5 p-3"
                data-testid="unlock-modal-error"
              >
                <p className="text-sm text-error">
                  {view.reason === 'in_progress'
                    ? t('errors.inProgress')
                    : (resolveDeclineCopy(view.message, tBilling) ??
                      t('errors.charge'))}
                </p>
                {view.reason === 'decline' ? (
                  <button
                    type="button"
                    className="self-start text-sm text-primary underline"
                    onClick={handleUpdateCard}
                    data-testid="unlock-modal-update-card"
                  >
                    {t('errors.updateCard')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="LeadAgentUnlockModal__Actions p-4 bg-base-200 flex gap-3">
            <Button
              color="primary"
              size="sm"
              className="rounded flex-1"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              loading={isProcessing}
              data-testid="unlock-modal-submit"
            >
              {view.status === 'error'
                ? t('confirm.retrySubmit')
                : amountLabel
                  ? t(`confirm.submit.${phaseKey}`, { amount: amountLabel })
                  : t('confirm.submitFallback')}
            </Button>
            <Button
              color="ghost"
              size="sm"
              className="rounded"
              onClick={handleClose}
              disabled={isProcessing}
              data-testid="unlock-modal-cancel"
            >
              {t('confirm.cancel')}
            </Button>
          </div>
        </>
      ) : null}
    </Modal.Legacy>
  );
}
